// Resident photo slideshow data layer (Cloud Storage + Firestore).
// Each photo is its own object at
//   tenants/{tenantId}/residents/{residentId}/photos/{photoId}
// with a matching Firestore doc at residentPhotos/{residentId}/items/{photoId}
// carrying the download URL + who added it + a timestamp. Every client reads the
// items subcollection and cycles through them as a slideshow. Any family member
// may add a slide; deleting a slide removes its object + doc.

import { collection, doc, setDoc, updateDoc, deleteDoc, serverTimestamp } from "firebase/firestore";
import { getStorage, ref as storageRef, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { v4 as uuidv4 } from "uuid";
import app, { db } from "../firebase/client";

const storage = () => getStorage(app);
const photoStoragePath = (tenantId, residentId, photoId) =>
  `tenants/${tenantId}/residents/${residentId}/photos/${photoId}`;
export const photoItemsCol = (residentId) => collection(db, "residentPhotos", residentId, "items");
const photoItemDoc = (residentId, photoId) => doc(db, "residentPhotos", residentId, "items", photoId);

// Downscale a chosen image to a sane size (longest edge <= 1280px) and re-encode
// as JPEG — keeps storage/bandwidth small and strips most metadata.
function downscale(file, maxEdge = 1280, quality = 0.85) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      let { width, height } = img;
      const scale = Math.min(1, maxEdge / Math.max(width, height));
      width = Math.round(width * scale);
      height = Math.round(height * scale);
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      canvas.getContext("2d").drawImage(img, 0, 0, width, height);
      canvas.toBlob(
        (blob) => (blob ? resolve(blob) : reject(new Error("Could not process image"))),
        "image/jpeg",
        quality
      );
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("Could not read image")); };
    img.src = url;
  });
}

// Add one photo (a new slide) to the resident's slideshow. Caption optional.
export async function addResidentPhoto({ tenantId, residentId, file, authorUid, authorName, caption }) {
  const blob = await downscale(file);
  const photoId = uuidv4();
  const path = photoStoragePath(tenantId, residentId, photoId);
  const ref = storageRef(storage(), path);
  await uploadBytes(ref, blob, { contentType: "image/jpeg" });
  const url = await getDownloadURL(ref);
  // addedBy lets the push trigger skip notifying the uploader; addedByName is
  // shown in the photo dialog; caption is shown on the slideshow.
  await setDoc(photoItemDoc(residentId, photoId), {
    tenantId, url, storagePath: path,
    addedBy: authorUid || null,
    addedByName: authorName || null,
    caption: (caption || "").trim() || null,
    createdAt: serverTimestamp(),
  });
  return { id: photoId, url };
}

// Add / change / clear a photo's caption. Records who made the edit (updatedBy)
// so the activity log can attribute it, not just the original uploader.
export async function updateResidentPhotoCaption({ residentId, photoId, caption, authorUid, authorName }) {
  await updateDoc(photoItemDoc(residentId, photoId), {
    caption: (caption || "").trim() || null,
    updatedBy: authorUid || null,
    updatedByName: authorName || null,
  });
}

// Remove one slide: the Storage object + its Firestore doc.
export async function deleteResidentPhoto({ residentId, photoId, storagePath }) {
  if (storagePath) {
    try { await deleteObject(storageRef(storage(), storagePath)); }
    catch (_) { /* object may already be gone */ }
  }
  await deleteDoc(photoItemDoc(residentId, photoId)).catch(() => {});
}
